import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Chip,
  Tooltip,
  Stack,
} from "@mui/material";
import { getBanks, addUserBanks } from "../../../../../services/dashboard/banks";
import { fetchUserBanks } from "../../../../../services/Users";

// تسميات الصلاحيات بالعربية (بيانات ثابتة)
const permissionLabels = {
  deposit: "إيداع",
  deposit_pay: "استخدام للفواتير",
  withdrawn: "سحب",
  move: "تحويل",
  show: "عرض",
  hide_total: "إخفاء الإجمالي",
};

const UserBankPermissionsForm = ({ userId, onSuccess }) => {
  // صلاحيات مُحددة محلياً
  const [selectedPerms, setSelectedPerms] = useState(
    Object.keys(permissionLabels).reduce((acc, k) => ((acc[k] = false), acc), {})
  );
  // خزن مأخوذة من API
  const [banks, setBanks] = useState([]);
  // صلاحيات المستخدم الحالية (جلب عبر fetchUserBanks)
  const [userBanks, setUserBanks] = useState([]);
  const [loadingUserBanks, setLoadingUserBanks] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [selectedBanks, setSelectedBanks] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // استخرج حالة الـ selectedPerms من userBanks: إذا أي خزنة تملك الصلاحية فليكن true
  const updateSelectedPermsFromUserBanks = (ubs = []) => {
    const next = Object.keys(permissionLabels).reduce((acc, k) => {
      acc[k] = ubs.some((ub) => Number(ub[k] || 0) === 1);
      return acc;
    }, {});
    setSelectedPerms((prev) => ({ ...prev, ...next }));
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoadingBanks(true);
      setError(null);
      try {
        const resp = await getBanks();
        // getBanks في الخدمة تعيد data أو مصفوفة؛ نتعامل مع الشكل الآمن
        const list = resp?.data || resp;
        if (!mounted) return;
        setBanks(Array.isArray(list) ? list : []);
        // جلب صلاحيات المستخدم الحالية إن وُجد userId
        if (userId) {
          setLoadingUserBanks(true);
          try {
            const ubResp = await fetchUserBanks(userId);
            const ubList = ubResp?.data || ubResp;
            if (!mounted) return;
            const normalized = Array.isArray(ubList) ? ubList : [];
            setUserBanks(normalized);
            // حدِّث checkboxes طبقاً لما جلبناه
            updateSelectedPermsFromUserBanks(normalized);
          } catch (e) {
            // لا نكسر الواجهة، نعرض فقط خطأ عام
            // يمكنك تعيين setError إذا أردت عرض الخطأ هنا
          } finally {
            if (mounted) setLoadingUserBanks(false);
          }
        }
      } catch (err) {
        if (!mounted) return;
        setError(err.message || "فشل في جلب الخزن");
      } finally {
        if (!mounted) return;
        setLoadingBanks(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [userId]);

  // مرشح الخزن على أساس النص
  const filteredBanks = banks.filter((b) =>
    (b.name || "").toLowerCase().includes(filterText.trim().toLowerCase())
  );

  const selectAllFiltered = () => {
    setSelectedBanks((prev) => {
      const next = new Set(prev);
      filteredBanks.forEach((b) => next.add(b.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedBanks(new Set());

  const togglePerm = (key) => {
    setSelectedPerms((p) => ({ ...p, [key]: !p[key] }));
  };

  const toggleBankSelection = (bankId) => {
    setSelectedBanks((prev) => {
      const next = new Set(prev);
      if (next.has(bankId)) next.delete(bankId);
      else next.add(bankId);
      return next;
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    if (!userId) {
      setError("معرف المستخدم غير محدد");
      return;
    }
    if (!selectedBanks.size) {
      setError("اختر خزنًا واحدة على الأقل");
      return;
    }
    const anyPerm = Object.values(selectedPerms).some(Boolean);
    if (!anyPerm) {
      setError("اختر صلاحية واحدة على الأقل");
      return;
    }

const bankIdsArray = Array.from(selectedBanks);

// السيرفر متوقع permissions كـ object واحد
const permissionsObject = Object.fromEntries(
  Object.entries(selectedPerms).map(([k, v]) => [k, v ? 1 : 0])
);

const payload = {
  user_id: userId,
  bank_ids: bankIdsArray,
  permissions: permissionsObject,
};

    setSaving(true);
    try {
      await addUserBanks(payload);
      setSuccess("تم حفظ الصلاحيات بنجاح");
      // بعد الحفظ، جلب الصلاحيات المحدثة من الخادم
      if (userId) {
        setLoadingUserBanks(true);
        try {
          const ubResp = await fetchUserBanks(userId);
          const ubList = ubResp?.data || ubResp;
          const normalized = Array.isArray(ubList) ? ubList : [];
          setUserBanks(normalized);
          // حدِّث checkboxes طبقاً لما جلبناه بعد الحفظ أيضاً
          updateSelectedPermsFromUserBanks(normalized);
        } catch (e) {
          // لا نوقف العملية؛ يمكن تسجيل الخطأ إن أردت
        } finally {
          setLoadingUserBanks(false);
        }
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message || "فشل في حفظ الصلاحيات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }} variant="outlined">
      <Typography variant="h6" gutterBottom>
        إعداد صلاحيات الخزن للمستخدم
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>اختر الصلاحيات</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {Object.entries(permissionLabels).map(([key, label]) => (
            <FormControlLabel
              key={key}
              control={<Checkbox size="small" checked={!!selectedPerms[key]} onChange={() => togglePerm(key)} />}
              label={<Typography variant="caption">{label}</Typography>}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography variant="subtitle2">اختر الخزن</Typography>
          <TextField
            size="small"
            placeholder="بحث..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            sx={{ ml: 1, width: 200 }}
          />
          <Stack direction="row" spacing={1} sx={{ ml: "auto" }}>
            <Button size="small" onClick={selectAllFiltered}>تحديد الكل الظاهر ({filteredBanks.length})</Button>
            <Button size="small" onClick={clearSelection}>مسح</Button>
          </Stack>
        </Box>

        {loadingBanks ? (
          <CircularProgress size={20} />
        ) : (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            {filteredBanks.map((b) => {
              const ub = userBanks.find((u) => u.id === b.id) || null;
              const enabledKeys = ub ? Object.keys(permissionLabels).filter((k) => Number(ub[k] || 0)) : [];
              return (
                <Tooltip key={b.id} title={b.roots?.map((r) => r.name).join("، ") || ""}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={b.name}
                      size="small"
                      clickable
                      color={selectedBanks.has(b.id) ? "primary" : "default"}
                      onClick={() => toggleBankSelection(b.id)}
                      sx={{ maxWidth: 220 }}
                    />
                    {/* عرض مصغر مدمج للصلاحيات المفعّلة على كل خزنة */}
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", ml: 0.5 }}>
                      {enabledKeys.map((k) => (
                        <Chip
                          key={k}
                          label={permissionLabels[k]}
                          size="small"
                          variant="outlined"
                          color="success"
                          sx={{ fontSize: 11 }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
        <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
          محدد: {selectedBanks.size}
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Button variant="contained" onClick={handleSubmit} disabled={saving}>
        {saving ? <CircularProgress size={18} /> : "حفظ الصلاحيات"}
      </Button>

      {/* ملاحظة: تم إزالة عرض التفاصيل الفردية لصلاحيات المستخدم؛ الصلاحيات المفعلة معروضة مدمجة بجانب كل خزنة أعلاه */}
    </Paper>
  );
};

export default UserBankPermissionsForm;
